import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Target, Loader2, Sparkles, History, Calendar, Download, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MD_COMPONENTS } from "@/pages/AiAnalyticsPage";

const VERDICT = {
  ok: { label: "En orden", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  ajustar: { label: "Para ajustar", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  urgente: { label: "Urgente", cls: "bg-rose-100 text-rose-700 border-rose-200" },
};
const KIND = { weekly: "Semanal", monthly: "Mensual" };
const BASE = process.env.REACT_APP_BACKEND_URL;

const VerdictBadge = ({ v, small, testId }) => {
  const s = VERDICT[v] || VERDICT.ajustar;
  return (
    <span data-testid={testId}
      className={`${small ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"} font-semibold rounded-full border ${s.cls}`}>
      {s.label}
    </span>
  );
};

const HistoryList = ({ items, onOpen }) => (
  <div className="space-y-2" data-testid="paid-history">
    <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold flex items-center gap-1.5">
      <History className="w-3.5 h-3.5" /> Análisis anteriores
    </div>
    <div className="divide-y divide-slate-100 bg-white border border-slate-200 rounded-lg">
      {items.map((h) => (
        <button key={h.id} onClick={() => onOpen(h.id)} data-testid={`paid-history-${h.id}`}
          className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">{KIND[h.kind]}</span>
            {h.date_from} → {h.date_to}
            {h.report?.veredicto && <VerdictBadge v={h.report.veredicto} small />}
          </div>
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{h.report?.resumen}</p>
        </button>
      ))}
    </div>
  </div>
);

const ReportView = ({ doc }) => (
  <div className="space-y-4" data-testid="paid-report">
    <div className="flex items-center gap-3 flex-wrap">
      <h2 className="text-lg font-semibold text-slate-900">{doc.client_name}</h2>
      <span className="text-xs text-slate-500 flex items-center gap-1">
        <Calendar className="w-3 h-3" /> {KIND[doc.kind]} · {doc.date_from} → {doc.date_to}
      </span>
      <VerdictBadge v={doc.report?.veredicto} testId="paid-verdict" />
      <a href={`${BASE}/api/public/paid-reports/${doc.id}/pdf`} target="_blank" rel="noreferrer"
        data-testid="paid-download-pdf"
        className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-indigo-700 hover:text-indigo-900">
        <Download className="w-3.5 h-3.5" /> Descargar PDF
      </a>
    </div>
    <div className="p-6 md:p-8 bg-white border border-slate-200 rounded-lg max-w-5xl" data-testid="paid-narrative">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{doc.report?.markdown || ""}</ReactMarkdown>
    </div>
  </div>
);

export default function AiPaidPage() {
  const qc = useQueryClient();
  const [clientId, setClientId] = useState("");
  const [kind, setKind] = useState("weekly");
  const [jobId, setJobId] = useState(null);
  const [active, setActive] = useState(null);

  const clients = useQuery({
    queryKey: ["paid-clients"],
    queryFn: () => api.get("/admin/ai/paid/eligible-clients").then((r) => r.data),
  }).data || [];
  const history = useQuery({
    queryKey: ["paid-reports", clientId],
    queryFn: () => api.get("/admin/ai/paid/reports", { params: { client_id: clientId } }).then((r) => r.data),
    enabled: !!clientId,
  }).data || [];
  const jobQ = useQuery({
    queryKey: ["paid-job", jobId],
    queryFn: () => api.get(`/admin/ai/paid/jobs/${jobId}`).then((r) => r.data),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data?.status === "running" ? 4000 : false),
  });

  const openReport = (id) => api.get(`/admin/ai/paid/reports/${id}`).then((r) => setActive(r.data));

  React.useEffect(() => {
    const j = jobQ.data;
    if (!j || !jobId) return;
    if (j.status === "done") {
      openReport(j.report_id);
      setJobId(null);
      qc.invalidateQueries({ queryKey: ["paid-reports", clientId] });
      toast.success("Análisis listo");
    } else if (j.status === "error") {
      setJobId(null);
      toast.error(j.error || "Falló el análisis");
    }
  }, [jobQ.data, jobId, clientId, qc]);

  const run = async () => {
    try {
      const { data } = await api.post("/admin/ai/paid/analyze", { client_id: clientId, kind });
      setJobId(data.job_id);
      setActive(null);
      toast.info("Analizando Google Ads + Search Console + Analytics…");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo iniciar el análisis");
    }
  };
  const running = !!jobId;

  return (
    <div className="space-y-6" data-testid="paid-analyst-page">
      <div className="flex items-start gap-4 pb-2 border-b border-slate-200">
        <div className="h-12 w-12 rounded-md bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shrink-0">
          <Target className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-orange-700 font-semibold">Analista Paid</div>
          <h1 className="font-display text-2xl font-semibold text-slate-900 tracking-tight">Google Ads con metodología Narwhal</h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Cruza campañas, términos de búsqueda, placements de Display y PMax, cuota de impresiones, Search Console y Analytics
            para decirle al equipo de paid qué corregir.
          </p>
          <p className="text-[11px] text-slate-500 mt-2 flex items-center gap-1.5" data-testid="paid-schedule-note">
            <CalendarClock className="w-3.5 h-3.5" /> Automático: semanal los lunes 9 hs y mensual el día 1, con aviso por mail al equipo.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-4 bg-white border border-slate-200 rounded-lg">
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600">Cliente</label>
          <Select value={clientId} onValueChange={(v) => { setClientId(v); setActive(null); }}>
            <SelectTrigger className="w-[220px]" data-testid="paid-client-select"><SelectValue placeholder="Elegir cliente…" /></SelectTrigger>
            <SelectContent>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-600">Tipo</label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-[220px]" data-testid="paid-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Semanal (semana pasada)</SelectItem>
              <SelectItem value="monthly">Mensual (mes pasado)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={running || !clientId} data-testid="paid-run-btn"
          className="bg-orange-600 hover:bg-orange-700 text-white">
          {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analizando…</> : <><Sparkles className="w-4 h-4 mr-2" /> Analizar ahora</>}
        </Button>
      </div>

      {running && (
        <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-lg text-orange-800 text-sm" data-testid="paid-running-banner">
          <Loader2 className="w-4 h-4 animate-spin" />
          {jobQ.data?.stage === "analyzing" ? "Claude está analizando la cuenta… (puede tardar 1 a 2 minutos)" : "Descargando datos de Google Ads, Search Console y Analytics…"}
        </div>
      )}

      {active && <ReportView doc={active} />}
      {clientId && history.length > 0 && <HistoryList items={history} onOpen={openReport} />}
    </div>
  );
}
